import React from 'react';
import './PendingPaymentModal.css';

/**
 * Pending Payment Modal - Resume Payment Prompt
 * Displays when user has an incomplete payment session
 */
const PendingPaymentModal = ({ isOpen, paymentData, onResume, onCancel }) => {
  if (!isOpen || !paymentData) return null;

  const { planName, planPrice, gateway, timestamp } = paymentData;
  
  // Calculate time elapsed
  const timeElapsed = Date.now() - timestamp;
  const minutesElapsed = Math.floor(timeElapsed / 60000);
  const timeDisplay = minutesElapsed < 1 
    ? 'Just now' 
    : minutesElapsed === 1 
    ? '1 minute ago' 
    : `${minutesElapsed} minutes ago`;

  // Get gateway display name and icon
  const getGatewayInfo = (gw) => {
    const gateways = {
      'mobile_payment': { name: 'Mobile Money', icon: 'fa-mobile-alt', color: '#00c853' },
      'paystack': { name: 'Card Payment', icon: 'fa-credit-card', color: '#00d9ff' },
      'bank_transfer': { name: 'Bank Transfer', icon: 'fa-university', color: '#ffb800' }
    };
    return gateways[gw] || { name: 'Payment', icon: 'fa-shopping-cart', color: '#00d9ff' };
  };

  const gatewayInfo = getGatewayInfo(gateway);

  return (
    <div className="pending-modal-overlay" onClick={onCancel}>
      <div className="pending-modal-container" onClick={e => e.stopPropagation()}>
        {/* Animated Background Effects */}
        <div className="pending-modal-bg-effects">
          <div className="bg-grid"></div>
          <div className="bg-glow"></div>
          <div className="bg-particles">
            {[...Array(6)].map((_, i) => (
              <span key={i} className={`particle p${i + 1}`}></span>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="pending-modal-header">
          <div className="pending-icon-wrapper">
            <div className="pending-icon-pulse"></div>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h2 className="pending-title">
            <span className="glitch-text" data-text="INCOMPLETE PAYMENT">INCOMPLETE PAYMENT</span>
          </h2>
          <p className="pending-subtitle">We found a pending transaction in your session</p>
        </div>

        {/* Body */}
        <div className="pending-modal-body">
          {/* Payment Details Card */}
          <div className="payment-details-card">
            <div className="details-header">
              <i className="fas fa-receipt"></i>
              <span>Transaction Details</span>
            </div>
            
            <div className="details-content">
              <div className="detail-row">
                <span className="detail-label">
                  <i className="fas fa-crown"></i> Plan
                </span>
                <span className="detail-value plan-name">{planName}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">
                  <i className="fas fa-money-bill-wave"></i> Amount
                </span>
                <span className="detail-value amount-value">GH₵{planPrice}.00</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">
                  <i className={`fas ${gatewayInfo.icon}`}></i> Method
                </span>
                <span className="detail-value gateway-value" style={{ color: gatewayInfo.color }}>
                  {gatewayInfo.name}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">
                  <i className="fas fa-clock"></i> Started
                </span>
                <span className="detail-value time-value">{timeDisplay}</span>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="session-progress">
              <div className="progress-label">
                <span>Session expires in</span>
                <span className="time-remaining">{30 - minutesElapsed} minutes</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((30 - minutesElapsed) / 30) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="pending-info-box">
            <i className="fas fa-info-circle"></i>
            <p>Your payment details are saved securely. Resume to continue where you left off.</p>
          </div>
        </div>

        {/* Footer / Actions */}
        <div className="pending-modal-footer">
          <button className="btn-cancel" onClick={onCancel}>
            <span className="btn-content">
              <i className="fas fa-trash-alt"></i>
              <span className="btn-text">
                <span className="btn-main-text">Cancel Payment</span>
                <span className="btn-sub-text">Clear session</span>
              </span>
            </span>
          </button>
          
          <button className="btn-resume" onClick={onResume}>
            <span className="btn-content">
              <i className="fas fa-play-circle"></i>
              <span className="btn-text">
                <span className="btn-main-text">Resume Payment</span>
                <span className="btn-sub-text">Continue checkout</span>
              </span>
            </span>
            <div className="btn-shine"></div>
          </button>
        </div>

        {/* Security Badge */}
        <div className="security-badge">
          <i className="fas fa-shield-alt"></i>
          <span>Secured by Kamelot</span>
        </div>
      </div>
    </div>
  );
};

export default PendingPaymentModal;
